import React, { FC, ReactNode } from 'react'

import { LinariaClassName, css, cx } from '@linaria/core'
import { styled } from '@linaria/react'

import { Props, Typography } from '../../designSystem/Typography/Typography'

export type Gap = 4 | 8

export const Name: FC<
  {
    name: string
    age?: number | null
    statusInline?: ReactNode
    statusFooter?: ReactNode
    singleLine: boolean
    gap: Gap
  } & Props
> = ({
  name,
  age,
  fontSize = 32,
  fontWeight,
  statusFooter,
  statusInline,
  singleLine,
  gap = 4,
}) => {
  const styleProps = {
    fontSize,
    fontWeight,
  }

  const _name = name.split(' ')
  const _age = Boolean(age) && <Age age={age!} />

  if (!singleLine && _name.length > 1) {
    const [firstPartName, ...restName] = _name

    return (
      <Parent className={cx(gapStyle[gap])}>
        <Body {...styleProps}>
          <ParentName>
            <Truncated data-name="item-title-first-name">
              {firstPartName}
            </Truncated>
          </ParentName>
          <ParentName className={cx(gapStyle[gap])}>
            <Truncated data-name="item-title-last-name">
              {restName.join(' ')}
              {_age && <>,</>}
            </Truncated>
            {_age}
            {statusInline}
          </ParentName>
        </Body>
        {statusFooter}
      </Parent>
    )
  }

  return (
    <Parent className={cx(gapStyle[gap])}>
      <Body {...styleProps}>
        <ParentName className={cx(gapStyle[gap])}>
          <Truncated data-name="item-title-name">
            {name}
            {_age && <>,</>}
          </Truncated>
          {_age}
          <StatusInlineContainer>{statusInline}</StatusInlineContainer>
        </ParentName>
      </Body>
      {statusFooter}
    </Parent>
  )
}

const Age: FC<{ age: number }> = ({ age }) => {
  return <span data-name="item-title-age">{age}</span>
}

const Parent = styled.div`
  display: grid;
`

const Body = styled(Typography)`
  color: inherit;
`

const StatusInlineContainer = styled.span`
  display: flex;
  align-items: center;
  position: relative;

  /* Визуально выравниваем статус к тексту https://youtrack.mamba.ru/issue/M-9772 */
  top: -1px;
`

const ParentName = styled.span`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  max-width: fit-content;
  vertical-align: baseline;
`

const Truncated = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  /* https://stackoverflow.com/questions/35012943/text-overflow-hides-text-when-it-shouldnt-in-safari */

  &::after {
    content: '';
    display: inline-block;
    width: 0;
  }
`

const gapStyle: Record<Gap, LinariaClassName> = {
  4: css`
    gap: var(--spacing-4px);
    grid-gap: var(--spacing-4px);
  `,
  8: css`
    gap: var(--spacing-8px);
    grid-gap: var(--spacing-8px);
  `,
}
